import { schema } from '@octiga/microsoft-events/src/interfaces/management';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { getTargetGroupsIds } from '../Windows10CompliancePolicy';
import { assignmentsTargetGroups } from '../../intune-utlity';
import { AndroidCompliancePolicySchema } from './model';

function getValueOfCombinationOfCertifiedDeviceAndBasicIntegrity(schema: AndroidCompliancePolicySchema) {
    const securityRequireSafetyNetAttestationBasicIntegrity =
        schema.contains.properties.policy.properties.securityRequireSafetyNetAttestationBasicIntegrity?.const;
    const securityRequireSafetyNetAttestationCertifiedDevice =
        schema.contains.properties.policy.properties.securityRequireSafetyNetAttestationCertifiedDevice?.const;

    if (!securityRequireSafetyNetAttestationBasicIntegrity && !securityRequireSafetyNetAttestationCertifiedDevice) {
        return 'notSet';
    } else if (
        securityRequireSafetyNetAttestationBasicIntegrity &&
        !securityRequireSafetyNetAttestationCertifiedDevice
    ) {
        return 'optionOne';
    } else if (
        securityRequireSafetyNetAttestationBasicIntegrity &&
        securityRequireSafetyNetAttestationCertifiedDevice
    ) {
        return 'optionTwo';
    }
}

export function initForm(schema: AndroidCompliancePolicySchema, fb: FormBuilder): FormGroup {
    let form = new FormGroup({});

    form = fb.group({
        displayName: [schema.contains.properties.policy.properties.displayName?.const, Validators.required],
        description: [schema.contains.properties.policy.properties.description?.const],
        passwordRequired: [schema.contains.properties.policy.properties.passwordRequired?.const],
        passwordRequiredType: [schema.contains.properties.policy.properties.passwordRequiredType?.const],
        passwordMinimumLength: [
            schema.contains.properties.policy.properties.passwordMinimumLength?.const,
            [Validators.min(4), Validators.max(16)],
        ],
        passwordMinutesOfInactivityBeforeLock: [
            schema.contains.properties.policy.properties.passwordMinutesOfInactivityBeforeLock?.const,
        ],
        passwordExpirationDays: [
            schema.contains.properties.policy.properties.passwordExpirationDays?.const,
            [Validators.min(1), Validators.max(365)],
        ],
        passwordPreviousPasswordCountToBlock: [
            schema.contains.properties.policy.properties.passwordPreviousPasswordCountToBlock?.const,
            [Validators.min(1), Validators.max(24)],
        ],
        storageRequireEncryption: [schema.contains.properties.policy.properties.storageRequireEncryption?.const],
        securityRequireIntuneAppIntegrity: [
            schema.contains.properties.policy.properties.securityRequireIntuneAppIntegrity?.const,
        ],
        osMinimumVersion: [
            schema.contains.properties.policy.properties.osMinimumVersion?.const,
            minmaxValidator(form.get('osMinimumVersion'), 'osMaximumVersion', 'max'),
        ],
        osMaximumVersion: [
            schema.contains.properties.policy.properties.osMaximumVersion?.const,
            minmaxValidator(form.get('osMaximumVersion'), 'osMinimumVersion', 'min'),
        ],
        minAndroidSecurityPatchLevel: [
            schema.contains?.properties?.policy?.properties?.minAndroidSecurityPatchLevel?.const,
        ],
        deviceThreatProtectionRequiredSecurityLevel: [
            schema.contains.properties.policy.properties.deviceThreatProtectionRequiredSecurityLevel?.const,
        ],
        advancedThreatProtectionRequiredSecurityLevel: [
            schema.contains.properties.policy.properties.advancedThreatProtectionRequiredSecurityLevel?.const,
        ],

        securityRequireSafetyNetAttestationBasicIntegrity: [
            schema.contains.properties.policy.properties.securityRequireSafetyNetAttestationBasicIntegrity?.const,
        ],
        securityRequireSafetyNetAttestationCertifiedDevice: [
            schema.contains.properties.policy.properties.securityRequireSafetyNetAttestationCertifiedDevice?.const,
        ],

        combinationOfCertifiedDeviceAndBasicIntegrity: [
            getValueOfCombinationOfCertifiedDeviceAndBasicIntegrity(schema),
        ],

        //playIntegrityVerdict: [schema.contains.properties.policy.properties.playIntegrityVerdict?.const]

        // add other field here

        assignments: fb.group({
            allUsers: [
                schema.contains.properties?.assignments?.items?.properties?.target?.oneOf?.some(
                    (res) => res?.properties?.['@odata.type']?.const === assignmentsTargetGroups.allUsers,
                ),
            ],
            allDevices: [
                schema.contains.properties?.assignments?.items?.properties?.target?.oneOf?.some(
                    (res) => res?.properties?.['@odata.type']?.const === assignmentsTargetGroups.allDevices,
                ),
            ],
            includedGroups: [getTargetGroupsIds(schema, assignmentsTargetGroups.groupInclusion)],
            groupInclusion: [
                schema.contains.properties?.assignments?.items?.properties?.target?.oneOf?.some(
                    (res) => res?.properties?.['@odata.type']?.const === assignmentsTargetGroups.groupInclusion,
                ),
            ],
        }),
    });

    form.get('passwordRequired')?.valueChanges.subscribe((isPasswordRequired) => {
        const passwordRequiredTypeControl = form.get('passwordRequiredType');
        const passwordMinimumLengthControl = form.get('passwordMinimumLength');
        const passwordMinutesOfInactivityBeforeLockControl = form.get('passwordMinutesOfInactivityBeforeLock');
        const passwordExpirationDaysControl = form.get('passwordExpirationDays');
        const passwordPreviousPasswordCountToBlockControl = form.get('passwordPreviousPasswordCountToBlock');

        if (isPasswordRequired) {
            passwordRequiredTypeControl?.addValidators(Validators.required);
            passwordRequiredTypeControl?.setValue('numeric');

            passwordMinimumLengthControl?.addValidators(Validators.required);
            passwordMinimumLengthControl?.setValue(4);

            passwordMinutesOfInactivityBeforeLockControl?.addValidators(Validators.required);
            passwordMinutesOfInactivityBeforeLockControl?.setValue(15);

            passwordExpirationDaysControl?.addValidators(Validators.required);
            passwordExpirationDaysControl?.setValue(365);

            passwordPreviousPasswordCountToBlockControl?.addValidators(Validators.required);
            passwordPreviousPasswordCountToBlockControl?.setValue(5);
        } else {
            passwordRequiredTypeControl?.removeValidators(Validators.required);
            passwordMinimumLengthControl?.removeValidators(Validators.required);
            passwordMinutesOfInactivityBeforeLockControl?.removeValidators(Validators.required);
            passwordExpirationDaysControl?.removeValidators(Validators.required);
            passwordPreviousPasswordCountToBlockControl?.removeValidators(Validators.required);
        }
        passwordRequiredTypeControl?.updateValueAndValidity();
    });

    return form;
}

export function minmaxValidator(control: AbstractControl, otherControlName: string, type: 'min' | 'max'): any {
    const validate = (control: AbstractControl) => {
        if (!control?.parent || !control) {
            return null;
        }

        const otherControlValue = control.parent.get(otherControlName).value;
        if (!control.value || !otherControlValue) {
            return null;
        }

        if (type === 'min') {
            if (getComparableNumber(control.value) < getComparableNumber(otherControlValue)) {
                return { min: { requiredMin: otherControlValue } };
            }
        } else {
            if (getComparableNumber(control.value) > getComparableNumber(otherControlValue)) {
                return { max: { requiredMin: otherControlValue } };
            }
        }

        return null;
    };
    return validate;
}

function getComparableNumber(value: string) {
    return +value.replaceAll('.', '');
}
